import axios, { AxiosResponse } from 'axios';
import { message } from 'antd';
import {
    ApiResponse,
    LoginRequest,
    LoginResponse,
    RegisterRequest,
    ChangePasswordRequest,
    ChangePasswordResponse,
    TagListRequest,
    TagListResponse,
    CreateTagRequest,
    CreateTagResponse,
    UpdateTagRequest,
    UpdateTagResponse,
    DeleteTagRequest,
    DeleteTagResponse,
    UntaggedImagesRequest,
    UntaggedImagesResponse,
    ImageListRequest,
    ImageListResponse,
    AdminImageListRequest,
    DeleteImageRequest,
    DeleteImageResponse,
    BatchDeleteImageRequest,
    BatchDeleteImageResponse,
    AddImageTagRequest,
    AddImageTagResponse,
    RemoveImageTagRequest,
    RemoveImageTagResponse,
    UploadImageResponse,
    User,
    CreateDeviceTypeRequest,
    CreateDeviceTypeResponse,
    DeviceTypeListRequest,
    DeviceTypeListResponse,
    UpdateDeviceTypeRequest,
    UpdateDeviceTypeResponse,
    DeleteDeviceTypeRequest,
    DeleteDeviceTypeResponse,
    AddDeviceTypeTagRequest,
    AddDeviceTypeTagResponse,
    DeviceTypeTagListRequest,
    DeviceTypeTagListResponse,
    RemoveDeviceTypeTagRequest,
    RemoveDeviceTypeTagResponse,
    SortCategoryListRequest,
    SortCategoryListResponse,
    CreateSortCategoryRequest,
    CreateSortCategoryResponse,
    UpdateSortCategoryRequest,
    UpdateSortCategoryResponse,
    DeleteSortCategoryRequest,
    DeleteSortCategoryResponse,
    UpdateImageSortCategoryRequest,
    UpdateImageSortCategoryResponse,
    UpdateImageDistributionRequest,
    UpdateImageDistributionResponse,
    UpdateImageMultiScreenRequest,
    UpdateImageMultiScreenResponse,
    StatsRequest,
    StatsResponse,
    MonitorStatusRequest,
    MonitorStatusResponse,
    UploadRecordsListRequest,
    UploadRecordsListResponse,
    App,
    AppListRequest,
    AppListResponse,
    AppDetailRequest,
    AppDetailResponse,
    CreateAppRequest,
    CreateAppResponse,
    UpdateAppRequest,
    UpdateAppResponse,
    DeleteAppRequest,
    DeleteAppResponse,
    FirmwareVersion,
    FirmwareVersionListRequest,
    FirmwareVersionListResponse,
    FirmwareVersionDetailRequest,
    FirmwareVersionDetailResponse,
    CreateFirmwareVersionRequest,
    CreateFirmwareVersionResponse,
    UpdateFirmwareVersionRequest,
    UpdateFirmwareVersionResponse,
    DeleteFirmwareVersionRequest,
    DeleteFirmwareVersionResponse,
    BatchCreateFirmwareVersionRequest,
    BatchCreateFirmwareVersionResponse,
    AppVersion,
    AppVersionListRequest,
    AppVersionListResponse,
    CreateAppVersionRequest,
    CreateAppVersionResponse,
    UpdateAppVersionRequest,
    UpdateAppVersionResponse,
    DeleteAppVersionRequest,
    DeleteAppVersionResponse,
    Material,
    MaterialListRequest,
    MaterialListResponse,
    MaterialSearchRequest,
    MaterialUploadResponse,
    MaterialDeleteRequest,
    MaterialDeleteResponse,
    MaterialKeywordListRequest,
    MaterialKeywordListResponse,
    MaterialAddKeywordsRequest,
    MaterialAddKeywordsResponse,
    MaterialRemoveKeywordRequest,
    MaterialRemoveKeywordResponse,
    FlashWriterAuthCode,
    FlashWriterAuthCodeListRequest,
    FlashWriterAuthCodeListResponse,
    CreateFlashWriterAuthCodeRequest,
    CreateFlashWriterAuthCodeResponse,
    DeleteFlashWriterAuthCodeRequest,
    DeleteFlashWriterAuthCodeResponse
} from '../types';

// 用于处理401错误的回调函数，由应用程序在启动时设置
let onUnauthorized: (() => void) | null = null;

// 设置401错误处理回调
export const setUnauthorizedHandler = (handler: () => void) => {
    onUnauthorized = handler;
};

// 创建axios实例
const api = axios.create({
    baseURL: '/api', // 使用相对路径，通过代理转发到后端
    headers: {
        'Content-Type': 'application/json'
    }
});

// 请求拦截器：添加token
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// 响应拦截器：处理错误
api.interceptors.response.use(
    (response) => {
        // 检查响应数据中的code字段
        if (response.data?.code === 401) {
            // Token过期或无效
            message.error('登录已过期，请重新登录');

            // 清除本地存储
            localStorage.removeItem('token');
            localStorage.removeItem('user');

            // 调用外部设置的处理函数（通常是logout并导航到登录页）
            if (onUnauthorized) {
                onUnauthorized();
            } else {
                // 如果没有设置处理函数，则使用默认的页面跳转方式
                window.location.href = '/login';
            }
        }
        return response;
    },
    (error) => {
        if (error.response?.status === 401) {
            // HTTP状态码401
            message.error('登录已过期，请重新登录');

            // 清除本地存储
            localStorage.removeItem('token');
            localStorage.removeItem('user');

            // 调用外部设置的处理函数（通常是logout并导航到登录页）
            if (onUnauthorized) {
                onUnauthorized();
            } else {
                // 如果没有设置处理函数，则使用默认的页面跳转方式
                window.location.href = '/login';
            }
        }
        return Promise.reject(error);
    }
);

export class ApiService {
    // 用户注册
    static async register(data: RegisterRequest): Promise<ApiResponse<User>> {
        const response: AxiosResponse<ApiResponse<User>> = await api.post('/register', data);
        return response.data;
    }

    // 用户登录
    static async login(data: LoginRequest): Promise<ApiResponse<LoginResponse>> {
        const response: AxiosResponse<ApiResponse<LoginResponse>> = await api.post('/login', data);
        return response.data;
    }

    // 修改密码
    static async changePassword(data: ChangePasswordRequest): Promise<ApiResponse<ChangePasswordResponse>> {
        const response: AxiosResponse<ApiResponse<ChangePasswordResponse>> = await api.post('/change-password', data);
        return response.data;
    }

    // 获取标签列表
    static async getTagList(data: TagListRequest): Promise<ApiResponse<TagListResponse>> {
        const response: AxiosResponse<ApiResponse<TagListResponse>> = await api.post('/tags/list', data);
        return response.data;
    }

    // 创建标签
    static async createTag(data: CreateTagRequest): Promise<ApiResponse<CreateTagResponse>> {
        const response: AxiosResponse<ApiResponse<CreateTagResponse>> = await api.post('/tags/create', data);
        return response.data;
    }

    // 更新标签
    static async updateTag(data: UpdateTagRequest): Promise<ApiResponse<UpdateTagResponse>> {
        const response: AxiosResponse<ApiResponse<UpdateTagResponse>> = await api.post('/tags/update', data);
        return response.data;
    }

    // 删除标签
    static async deleteTag(data: DeleteTagRequest): Promise<ApiResponse<DeleteTagResponse>> {
        const response: AxiosResponse<ApiResponse<DeleteTagResponse>> = await api.post('/tags/delete', data);
        return response.data;
    }

    // 获取无标签图片列表
    static async getUntaggedImages(data: UntaggedImagesRequest): Promise<ApiResponse<UntaggedImagesResponse>> {
        const response: AxiosResponse<ApiResponse<UntaggedImagesResponse>> = await api.post('/images/untagged', data);
        return response.data;
    }

    // 新增：获取图片列表
    static async getImageList(data: ImageListRequest): Promise<ApiResponse<ImageListResponse>> {
        const response: AxiosResponse<ApiResponse<ImageListResponse>> = await api.post('/images/list', data);
        return response.data;
    }

    // 新增：获取管理员图片列表
    static async getAdminImageList(data: AdminImageListRequest): Promise<ApiResponse<ImageListResponse>> {
        const response: AxiosResponse<ApiResponse<ImageListResponse>> = await api.post('/images/admin-list', data);
        return response.data;
    }

    // 新增：删除图片
    static async deleteImage(data: DeleteImageRequest): Promise<ApiResponse<DeleteImageResponse>> {
        const response: AxiosResponse<ApiResponse<DeleteImageResponse>> = await api.post('/images/delete', data);
        return response.data;
    }

    // 新增：批量删除图片
    static async batchDeleteImages(data: BatchDeleteImageRequest): Promise<ApiResponse<BatchDeleteImageResponse>> {
        const response: AxiosResponse<ApiResponse<BatchDeleteImageResponse>> = await api.post('/images/batch-delete', data);
        return response.data;
    }

    // 为图片添加标签
    static async addImageTag(data: AddImageTagRequest): Promise<ApiResponse<AddImageTagResponse>> {
        const response: AxiosResponse<ApiResponse<AddImageTagResponse>> = await api.post('/images/tag', data);
        return response.data;
    }

    // 删除图片标签
    static async removeImageTag(data: RemoveImageTagRequest): Promise<ApiResponse<RemoveImageTagResponse>> {
        const response: AxiosResponse<ApiResponse<RemoveImageTagResponse>> = await api.post('/images/remove-tag', data);
        return response.data;
    }

    // 图片上传
    static async uploadImage(formData: FormData): Promise<ApiResponse<UploadImageResponse>> {
        const response: AxiosResponse<ApiResponse<UploadImageResponse>> = await api.post('/images/upload', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
        return response.data;
    }

    // 设备类型管理相关接口
    // 创建设备类型
    static async createDeviceType(data: CreateDeviceTypeRequest): Promise<ApiResponse<CreateDeviceTypeResponse>> {
        const response: AxiosResponse<ApiResponse<CreateDeviceTypeResponse>> = await api.post('/device-types/create', data);
        return response.data;
    }

    // 获取设备类型列表
    static async getDeviceTypeList(data: DeviceTypeListRequest): Promise<ApiResponse<DeviceTypeListResponse>> {
        const response: AxiosResponse<ApiResponse<DeviceTypeListResponse>> = await api.post('/device-types/list', data);
        return response.data;
    }

    // 更新设备类型
    static async updateDeviceType(data: UpdateDeviceTypeRequest): Promise<ApiResponse<UpdateDeviceTypeResponse>> {
        const response: AxiosResponse<ApiResponse<UpdateDeviceTypeResponse>> = await api.post('/device-types/update', data);
        return response.data;
    }

    // 删除设备类型
    static async deleteDeviceType(data: DeleteDeviceTypeRequest): Promise<ApiResponse<DeleteDeviceTypeResponse>> {
        const response: AxiosResponse<ApiResponse<DeleteDeviceTypeResponse>> = await api.post('/device-types/delete', data);
        return response.data;
    }

    // 设备类型标签管理相关接口
    // 为设备类型添加标签
    static async addDeviceTypeTag(data: AddDeviceTypeTagRequest): Promise<ApiResponse<AddDeviceTypeTagResponse>> {
        const response: AxiosResponse<ApiResponse<AddDeviceTypeTagResponse>> = await api.post('/device-types/tags/add', data);
        return response.data;
    }

    // 获取设备类型的标签列表
    static async getDeviceTypeTags(data: DeviceTypeTagListRequest): Promise<ApiResponse<DeviceTypeTagListResponse>> {
        const response: AxiosResponse<ApiResponse<DeviceTypeTagListResponse>> = await api.post('/device-types/tags/list', data);
        return response.data;
    }

    // 移除设备类型的标签
    static async removeDeviceTypeTag(data: RemoveDeviceTypeTagRequest): Promise<ApiResponse<RemoveDeviceTypeTagResponse>> {
        const response: AxiosResponse<ApiResponse<RemoveDeviceTypeTagResponse>> = await api.post('/device-types/tags/remove', data);
        return response.data;
    }

    // 排序类别管理相关接口
    // 获取排序类别列表
    static async getSortCategoryList(data: SortCategoryListRequest): Promise<ApiResponse<SortCategoryListResponse>> {
        const response: AxiosResponse<ApiResponse<SortCategoryListResponse>> = await api.post('/sort-categories/list', data);
        return response.data;
    }

    // 创建排序类别
    static async createSortCategory(data: CreateSortCategoryRequest): Promise<ApiResponse<CreateSortCategoryResponse>> {
        const response: AxiosResponse<ApiResponse<CreateSortCategoryResponse>> = await api.post('/sort-categories/create', data);
        return response.data;
    }

    // 更新排序类别
    static async updateSortCategory(data: UpdateSortCategoryRequest): Promise<ApiResponse<UpdateSortCategoryResponse>> {
        const response: AxiosResponse<ApiResponse<UpdateSortCategoryResponse>> = await api.post('/sort-categories/update', data);
        return response.data;
    }

    // 删除排序类别
    static async deleteSortCategory(data: DeleteSortCategoryRequest): Promise<ApiResponse<DeleteSortCategoryResponse>> {
        const response: AxiosResponse<ApiResponse<DeleteSortCategoryResponse>> = await api.post('/sort-categories/delete', data);
        return response.data;
    }

    // 修改图片排序类别
    static async updateImageSortCategory(data: UpdateImageSortCategoryRequest): Promise<ApiResponse<UpdateImageSortCategoryResponse>> {
        const response: AxiosResponse<ApiResponse<UpdateImageSortCategoryResponse>> = await api.post('/images/sort-category', data);
        return response.data;
    }

    // 修改图片分发状态
    static async updateImageDistribution(data: UpdateImageDistributionRequest): Promise<ApiResponse<UpdateImageDistributionResponse>> {
        const response: AxiosResponse<ApiResponse<UpdateImageDistributionResponse>> = await api.post('/images/distribution', data);
        return response.data;
    }

    // 修改图片双屏素材状态
    static async updateImageMultiScreen(data: UpdateImageMultiScreenRequest): Promise<ApiResponse<UpdateImageMultiScreenResponse>> {
        const response: AxiosResponse<ApiResponse<UpdateImageMultiScreenResponse>> = await api.post('/images/multi-screen', data);
        return response.data;
    }

    // 获取系统统计数据
    static async getStats(data: StatsRequest = {}): Promise<ApiResponse<StatsResponse>> {
        const response: AxiosResponse<ApiResponse<StatsResponse>> = await api.post('/stats', data);
        return response.data;
    }

    // 获取系统监控状态
    static async getMonitorStatus(data: MonitorStatusRequest = {}): Promise<ApiResponse<MonitorStatusResponse>> {
        const response: AxiosResponse<ApiResponse<MonitorStatusResponse>> = await api.post('/monitor/status', data);
        return response.data;
    }

    // 获取上传记录列表
    static async getUploadRecordsList(data: UploadRecordsListRequest): Promise<ApiResponse<UploadRecordsListResponse>> {
        const response: AxiosResponse<ApiResponse<UploadRecordsListResponse>> = await api.post('/upload-records/list', data);
        return response.data;
    }

    // 应用管理相关接口
    // 获取应用列表
    static async getAppList(data: AppListRequest): Promise<ApiResponse<AppListResponse>> {
        const response: AxiosResponse<ApiResponse<AppListResponse>> = await api.post('/apps/list', data);
        return response.data;
    }

    // 获取应用详情
    static async getAppDetail(id: number): Promise<ApiResponse<AppDetailResponse>> {
        const response: AxiosResponse<ApiResponse<AppDetailResponse>> = await api.get(`/apps/${id}`);
        return response.data;
    }

    // 创建应用
    static async createApp(data: CreateAppRequest): Promise<ApiResponse<CreateAppResponse>> {
        const response: AxiosResponse<ApiResponse<CreateAppResponse>> = await api.post('/apps/create', data);
        return response.data;
    }

    // 更新应用
    static async updateApp(data: UpdateAppRequest): Promise<ApiResponse<UpdateAppResponse>> {
        const response: AxiosResponse<ApiResponse<UpdateAppResponse>> = await api.post('/apps/update', data);
        return response.data;
    }

    // 删除应用
    static async deleteApp(data: DeleteAppRequest): Promise<ApiResponse<DeleteAppResponse>> {
        const response: AxiosResponse<ApiResponse<DeleteAppResponse>> = await api.post('/apps/delete', data);
        return response.data;
    }

    // 固件版本管理相关接口
    // 获取固件版本列表
    static async getFirmwareVersionList(data: FirmwareVersionListRequest): Promise<ApiResponse<FirmwareVersionListResponse>> {
        const response: AxiosResponse<ApiResponse<FirmwareVersionListResponse>> = await api.post('/firmware-versions/list', data);
        return response.data;
    }

    // 获取固件版本详情
    static async getFirmwareVersionDetail(id: number): Promise<ApiResponse<FirmwareVersionDetailResponse>> {
        const response: AxiosResponse<ApiResponse<FirmwareVersionDetailResponse>> = await api.get(`/firmware-versions/${id}`);
        return response.data;
    }

    // 创建固件版本
    static async createFirmwareVersion(data: CreateFirmwareVersionRequest): Promise<ApiResponse<CreateFirmwareVersionResponse>> {
        const response: AxiosResponse<ApiResponse<CreateFirmwareVersionResponse>> = await api.post('/firmware-versions/create', data);
        return response.data;
    }

    // 更新固件版本
    static async updateFirmwareVersion(data: UpdateFirmwareVersionRequest): Promise<ApiResponse<UpdateFirmwareVersionResponse>> {
        const response: AxiosResponse<ApiResponse<UpdateFirmwareVersionResponse>> = await api.post('/firmware-versions/update', data);
        return response.data;
    }

    // 删除固件版本
    static async deleteFirmwareVersion(data: DeleteFirmwareVersionRequest): Promise<ApiResponse<DeleteFirmwareVersionResponse>> {
        const response: AxiosResponse<ApiResponse<DeleteFirmwareVersionResponse>> = await api.post('/firmware-versions/delete', data);
        return response.data;
    }

    // 批量创建固件版本
    static async batchCreateFirmwareVersion(data: BatchCreateFirmwareVersionRequest): Promise<ApiResponse<BatchCreateFirmwareVersionResponse>> {
        const response: AxiosResponse<ApiResponse<BatchCreateFirmwareVersionResponse>> = await api.post('/firmware-versions/batch-create', data);
        return response.data;
    }

    // ===== 应用版本管理 =====
    // 获取应用版本列表
    static async getAppVersionList(data: AppVersionListRequest): Promise<ApiResponse<AppVersionListResponse>> {
        const response: AxiosResponse<ApiResponse<AppVersionListResponse>> = await api.post('/app-versions/list', data);
        return response.data;
    }

    // 获取应用版本详情
    static async getAppVersionDetail(id: number): Promise<ApiResponse<AppVersion>> {
        const response: AxiosResponse<ApiResponse<AppVersion>> = await api.get(`/app-versions/${id}`);
        return response.data;
    }

    // 创建应用版本
    static async createAppVersion(data: CreateAppVersionRequest): Promise<ApiResponse<CreateAppVersionResponse>> {
        const response: AxiosResponse<ApiResponse<CreateAppVersionResponse>> = await api.post('/app-versions/create', data);
        return response.data;
    }

    // 更新应用版本
    static async updateAppVersion(data: UpdateAppVersionRequest): Promise<ApiResponse<UpdateAppVersionResponse>> {
        const response: AxiosResponse<ApiResponse<UpdateAppVersionResponse>> = await api.post('/app-versions/update', data);
        return response.data;
    }

    // 删除应用版本
    static async deleteAppVersion(data: DeleteAppVersionRequest): Promise<ApiResponse<DeleteAppVersionResponse>> {
        const response: AxiosResponse<ApiResponse<DeleteAppVersionResponse>> = await api.post('/app-versions/delete', data);
        return response.data;
    }

    // ===== 素材管理相关接口 =====
    // 批量上传素材
    static async uploadMaterials(formData: FormData): Promise<ApiResponse<MaterialUploadResponse>> {
        const response: AxiosResponse<ApiResponse<MaterialUploadResponse>> = await api.post('/materials/upload', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
        return response.data;
    }

    // 获取素材列表
    static async getMaterialList(data: MaterialListRequest): Promise<ApiResponse<MaterialListResponse>> {
        const response: AxiosResponse<ApiResponse<MaterialListResponse>> = await api.post('/materials/list', data);
        return response.data;
    }

    // 按关键字搜索素材
    static async searchMaterials(data: MaterialSearchRequest): Promise<ApiResponse<MaterialListResponse>> {
        const response: AxiosResponse<ApiResponse<MaterialListResponse>> = await api.post('/materials/search', data);
        return response.data;
    }

    // 删除素材
    static async deleteMaterial(data: MaterialDeleteRequest): Promise<ApiResponse<MaterialDeleteResponse>> {
        const response: AxiosResponse<ApiResponse<MaterialDeleteResponse>> = await api.post('/materials/delete', data);
        return response.data;
    }

    // 获取关键字列表
    static async getMaterialKeywordList(data: MaterialKeywordListRequest): Promise<ApiResponse<MaterialKeywordListResponse>> {
        const response: AxiosResponse<ApiResponse<MaterialKeywordListResponse>> = await api.post('/materials/keywords/list', data);
        return response.data;
    }

    // 为素材添加关键字
    static async addMaterialKeywords(data: MaterialAddKeywordsRequest): Promise<ApiResponse<MaterialAddKeywordsResponse>> {
        const response: AxiosResponse<ApiResponse<MaterialAddKeywordsResponse>> = await api.post('/materials/keywords/add', data);
        return response.data;
    }

    // 删除素材关键字
    static async removeMaterialKeyword(data: MaterialRemoveKeywordRequest): Promise<ApiResponse<MaterialRemoveKeywordResponse>> {
        const response: AxiosResponse<ApiResponse<MaterialRemoveKeywordResponse>> = await api.post('/materials/keywords/remove', data);
        return response.data;
    }

    // ===== Flash Writer 授权码管理 =====
    // 获取授权码列表
    static async getFlashWriterAuthCodeList(data: FlashWriterAuthCodeListRequest): Promise<ApiResponse<FlashWriterAuthCodeListResponse>> {
        const response: AxiosResponse<ApiResponse<FlashWriterAuthCodeListResponse>> = await api.post('/flash-writer-auth-codes/list', data);
        return response.data;
    }

    // 创建授权码
    static async createFlashWriterAuthCode(data: CreateFlashWriterAuthCodeRequest): Promise<ApiResponse<CreateFlashWriterAuthCodeResponse>> {
        const response: AxiosResponse<ApiResponse<CreateFlashWriterAuthCodeResponse>> = await api.post('/flash-writer-auth-codes/create', data);
        return response.data;
    }

    // 删除授权码
    static async deleteFlashWriterAuthCode(data: DeleteFlashWriterAuthCodeRequest): Promise<ApiResponse<DeleteFlashWriterAuthCodeResponse>> {
        const response: AxiosResponse<ApiResponse<DeleteFlashWriterAuthCodeResponse>> = await api.post('/flash-writer-auth-codes/delete', data);
        return response.data;
    }
}

export default api; 